// Home as a grid of sections: 4 columns on desktop, 2 on tablet and phone. In edit mode each section
// gets a drag handle (pointer events, so it works with mouse and touch alike), a width picker and a
// hide button; the arrangement is saved as the "home_layout" preference only when the user taps Fine.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { EyeOff, GripVertical, Plus } from 'lucide-react';
import {
  HOME_SECTION_LABELS, HOME_TEMPLATES,
  type HomeCompactWidth, type HomeLayout, type HomeSectionId, type HomeWidth,
} from '@unitly/contracts';

const DESKTOP = '(min-width: 1024px)';

function useIsDesktop() {
  const [desktop, setDesktop] = useState(() => window.matchMedia(DESKTOP).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP);
    const on = () => setDesktop(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return desktop;
}

function move(layout: HomeLayout, id: HomeSectionId, targetId: HomeSectionId, after: boolean): HomeLayout {
  const sections = layout.sections.filter((s) => s.id !== id);
  const dragged = layout.sections.find((s) => s.id === id)!;
  const at = sections.findIndex((s) => s.id === targetId) + (after ? 1 : 0);
  sections.splice(at, 0, dragged);
  return { sections };
}

export function HomeGrid({ layout, editing, onChange, render, placeholder }: {
  layout: HomeLayout;
  editing: boolean;
  onChange: (layout: HomeLayout) => void;
  /** The section's content, or null when it has nothing to show right now. */
  render: (id: HomeSectionId) => ReactNode;
  /** What an empty section looks like while editing, so it can still be placed. */
  placeholder: (id: HomeSectionId) => ReactNode;
}) {
  const desktop = useIsDesktop();
  const [dragging, setDragging] = useState<HomeSectionId | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const pointer = useRef({ x: 0, y: 0 });

  // Scroll the page while a section is held near the top or bottom edge of the screen.
  useEffect(() => {
    if (!dragging) return;
    let frame = 0;
    const tick = () => {
      const { y } = pointer.current;
      const edge = 90;
      if (y < edge) window.scrollBy(0, -Math.ceil((edge - y) / 6));
      else if (y > window.innerHeight - edge) window.scrollBy(0, Math.ceil((y - window.innerHeight + edge) / 6));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [dragging]);

  const visible = layout.sections.filter((s) => s.visible);
  const hidden = layout.sections.filter((s) => !s.visible);
  const update = (id: HomeSectionId, patch: Partial<{ visible: boolean; width: HomeWidth; compactWidth: HomeCompactWidth }>) =>
    onChange({ sections: layout.sections.map((s) => (s.id === id ? { ...s, ...patch } : s)) });

  const dragTo = (x: number, y: number) => {
    if (!dragging) return;
    const cell = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-home-cell]');
    const target = cell?.dataset.homeCell as HomeSectionId | undefined;
    if (!cell || !target || target === dragging || !gridRef.current?.contains(cell)) return;
    const r = cell.getBoundingClientRect();
    // Cells sharing a row swap sideways; a full-width cell is entered from above or below.
    const wide = r.width > gridRef.current.clientWidth * 0.7;
    const after = wide ? y > r.top + r.height / 2 : x > r.left + r.width / 2;
    const next = move(layout, dragging, target, after);
    if (next.sections.some((s, i) => s.id !== layout.sections[i]!.id)) onChange(next);
  };

  const keyMove = (id: HomeSectionId, dir: -1 | 1) => {
    const i = visible.findIndex((s) => s.id === id);
    const other = visible[i + dir];
    if (other) onChange(move(layout, id, other.id, dir === 1));
  };

  const applyTemplate = (ids: HomeSectionId[]) => {
    const order = [...ids, ...layout.sections.map((s) => s.id).filter((id) => !ids.includes(id))];
    onChange({ sections: order.map((id) => ({ ...layout.sections.find((s) => s.id === id)!, visible: ids.includes(id) })) });
  };

  return (
    <>
      {editing && (
        <div className="home-templates chips" role="group" aria-label="Modelli">
          {HOME_TEMPLATES.map((t) => (
            <button key={t.id} type="button" className="chip" title={t.description} onClick={() => applyTemplate(t.sections)}>{t.name}</button>
          ))}
        </div>
      )}
      <div ref={gridRef} className={`home-grid${editing ? ' editing' : ''}${dragging ? ' is-dragging' : ''}`}>
        {visible.map((s) => {
          const content = render(s.id);
          if (!content && !editing) return null;
          return (
            <div
              key={s.id}
              data-home-cell={s.id}
              className={`home-cell${s.compactWidth === 2 ? ' compact-full' : ''}${dragging === s.id ? ' dragging' : ''}`}
              style={{ '--w': s.width, '--cw': s.compactWidth } as React.CSSProperties}
            >
              {editing && (
                <div className="home-cell-bar">
                  <button
                    type="button"
                    className="icon-btn muted home-drag"
                    aria-label={`Sposta ${HOME_SECTION_LABELS[s.id]} (frecce per spostare)`}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.currentTarget.setPointerCapture(e.pointerId);
                      pointer.current = { x: e.clientX, y: e.clientY };
                      setDragging(s.id);
                    }}
                    onPointerMove={(e) => {
                      if (dragging !== s.id) return;
                      pointer.current = { x: e.clientX, y: e.clientY };
                      dragTo(e.clientX, e.clientY);
                    }}
                    onPointerUp={() => setDragging(null)}
                    onPointerCancel={() => setDragging(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); keyMove(s.id, -1); }
                      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); keyMove(s.id, 1); }
                    }}
                  >
                    <GripVertical size={18} />
                  </button>
                  <strong className="grow">{HOME_SECTION_LABELS[s.id]}</strong>
                  <div className="segmented home-width" role="group" aria-label="Larghezza">
                    {(desktop ? [1, 2, 3, 4] : [1, 2]).map((n) => {
                      const on = (desktop ? s.width : s.compactWidth) === n;
                      return (
                        <button
                          key={n}
                          type="button"
                          aria-pressed={on}
                          aria-label={`${n} ${n === 1 ? 'colonna' : 'colonne'} su ${desktop ? 4 : 2}`}
                          onClick={() => update(s.id, desktop ? { width: n as HomeWidth } : { compactWidth: n as HomeCompactWidth })}
                        >{n}</button>
                      );
                    })}
                  </div>
                  <button type="button" className="icon-btn muted" aria-label={`Nascondi ${HOME_SECTION_LABELS[s.id]}`} onClick={() => update(s.id, { visible: false })}>
                    <EyeOff size={17} />
                  </button>
                </div>
              )}
              {content ?? placeholder(s.id)}
            </div>
          );
        })}
      </div>
      {editing && hidden.length > 0 && (
        <div className="home-hidden">
          <span className="hint">Sezioni nascoste</span>
          <div className="home-hidden-list">
            {hidden.map((s) => (
              <button key={s.id} type="button" className="chip" onClick={() => update(s.id, { visible: true })}>
                <Plus size={14} /> {HOME_SECTION_LABELS[s.id]}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
